export default {
  "app": {
    "title": "Game of Life"
  },
  "nav": {
    "home": "Home",
    "play": "Play",
    "explore": "Explore",
    "about": "About",
    "user": "User",
    "userMenu": "User menu",
    "profile": "Profile",
    "settings": "Settings",
    "signOut": "Sign out",
    "openMenu": "Open menu",
    "closeMenu": "Close menu",
    "toggleDarkMode": "Toggle dark mode",
    "viewSource": "View source on GitHub"
  },
  "footer": {
    "label": "Global footer",
    "blurb": "Conway's Game of Life playground",
    "sourceLink": "Open source on GitHub",
    "releaseLink": "Current release",
    "shortcuts": {
      "menuLabel": "Keyboard shortcuts",
      "title": "Keyboard shortcuts",
      "currentContext": "Current context: {{context}}",
      "focusHint": "Shortcut keys are active only when focus is not inside an input, select, textarea, or button.",
      "controlPrefix": "Control: {{control}}",
      "availableNow": "Available now",
      "allShortcuts": "All play page shortcuts",
      "noShortcutsHere": "No page-specific shortcuts are active in this view.",
      "currentBadge": "Current",
      "contextLanding": "Home page",
      "contextPlaySelect": "Play pattern picker",
      "contextPlayEdit": "Play page - Edit mode",
      "contextPlayRun": "Play page - Run mode",
      "contextExplore": "Explore page",
      "contextAbout": "About page",
      "contextProfile": "Profile page",
      "contextSettings": "Settings page",
      "actions": {
        "panBoard": "Pan board",
        "zoomBoard": "Zoom board in or out",
        "centerView": "Center board view",
        "toggleSimulation": "Start or pause simulation",
        "increaseSpeed": "Increase generation speed",
        "decreaseSpeed": "Decrease generation speed"
      },
      "controls": {
        "panControls": "Pan controls",
        "zoomControls": "Zoom in and Zoom out controls",
        "centerView": "Center view control",
        "startPause": "Start / Pause control",
        "increaseSpeed": "Increase generation speed control",
        "decreaseSpeed": "Decrease generation speed control"
      }
    }
  },
  "landing": {
    "eyebrow": "Cellular Automata Playground",
    "title": "Welcome to Game of Life",
    "description": "Explore emergent behavior from simple rules. Launch the interactive board, load starter patterns, and watch complexity unfold.",
    "enter": "Play game",
    "nextPattern": "Show next pattern",
    "patternCount": "{{current}} / {{total}}",
    "about": "Read About",
    "presetsTitle": "Try A Preset",
    "presetsDescription": "Jump directly into the board with a preloaded pattern.",
    "openPreset": "Open Preset",
    "presets": {
      "default": {
        "title": "R-Pentomino",
        "description": "The classic starter used in this app: small seed, surprisingly rich evolution."
      },
      "gosper": {
        "title": "Gosper Glider Gun",
        "description": "The first famous finite pattern that emits an endless stream of gliders."
      },
      "simkin": {
        "title": "Simkin Glider Gun",
        "description": "A compact period-120 glider gun and a modern counterpart to Gosper."
      }
    },
    "feature1": "Interactive infinite grid",
    "feature2": "Pattern presets and custom seeds",
    "feature3": "Rule toggles, diagnostics, and shareable URLs"
  },
  "controls": {
    "title": "Game Controls",
    "start": "Start",
    "pause": "Pause",
    "next": "Next",
    "reset": "Reset",
    "copyUrl": "Copy URL",
    "mode": "Mode",
    "playControls": "Play Controls",
    "editControls": "Edit Controls",
    "variables": "Game Variables",
    "generationSpeed": "Generation Speed",
    "generationSpeedDecrease": "Decrease generation speed",
    "generationSpeedIncrease": "Increase generation speed",
    "generationSpeedValue": "Generation speed {{level}}",
    "edit": "Edit",
    "editMode": "Edit Mode",
    "modeToggleHotkey": "M",
    "selection": "Select cells",
    "fillSelection": "Fill selected cells",
    "grabPan": "Grab pan",
    "stamp": "Stamp pattern",
    "stampPatterns": "Starter patterns",
    "currentStampSelection": "Current selection",
    "searchStampPatterns": "Search patterns",
    "filterStampCategory": "Filter by category",
    "allStampCategories": "All categories",
    "noStampPatternsFound": "No patterns match your search.",
    "rotateStamp": "Rotate stamp",
    "rotateStampHotkey": "[ / ] / R",
    "stampRotation": "{{degrees}} deg",
    "selectionCount": "{{count}} selected",
    "liveCellsSelected": "{{count}} live cells selected",
    "undo": "Undo",
    "pencil": "Pencil",
    "eraser": "Eraser",
    "drawColor": "Draw color",
    "systemColors": "System colors",
    "customColors": "Custom colors",
    "customColor": "Custom color",
    "customColorHex": "Custom color hex",
    "hoveredCell": "Cursor",
    "hoveredCellNone": "None",
    "miniMap": "Mini map",
    "miniMapViewport": "Current viewport",
    "maximizeBoard": "Maximize board",
    "restoreBoard": "Restore layout",
    "panUp": "Pan up",
    "panLeft": "Pan left",
    "centerView": "Center view",
    "panRight": "Pan right",
    "panDown": "Pan down",
    "zoomOut": "Zoom out",
    "zoomIn": "Zoom in",
    "editModeOn": "Edit mode is ON - Click cells to toggle",
    "editModeOff": "Edit mode is OFF"
  },
  "patterns": {
    "title": "Starter Patterns",
    "custom": "Custom Pattern",
    "loadCustom": "Load Custom Pattern",
    "placeholder": "Paste coordinates here...",
    "showExamples": "Show examples",
    "hideExamples": "Hide examples",
    "supportedFormats": "Supported formats:"
  },
  "colors": {
    "title": "Color Palette"
  },
  "rules": {
    "title": "Game Rules",
    "description": "Toggle rules to customize the simulation behavior",
    "standardSection": "Standard Rules",
    "experimentalSection": "Experimental Rules",
    "highlifeTag": "HighLife",
    "highlifeReproductionDescription": "Dead cells with exactly three or six live neighbors become live cells (HighLife B36 reproduction).",
    "lockButton": "Lock rules",
    "unlockButton": "Unlock rules",
    "lockedHint": "Rules are locked for this game. Unlock to edit.",
    "unlockedHint": "Rules are unlocked. Lock to prevent changes.",
    "unlockConfirmTitle": "Unlock game rules?",
    "unlockConfirmMessage": "Unlocking allows rule changes for this game. Continue?",
    "unlockConfirmAction": "Unlock"
  },
  "playground": {
    "copyHash": "Copy Hash",
    "save": "Save",
    "saveAs": "Save As",
    "saveOptions": "Save options",
    "fork": "Fork",
    "forkSuffix": "Fork",
    "savePrompt": "Save template as",
    "editTitle": "Edit template title",
    "forkTitle": "Name your fork",
    "createFork": "Create fork",
    "patternName": "Pattern name",
    "patternMetadata": "Pattern metadata",
    "infoTab": "Info",
    "infoTitle": "About",
    "infoAuthorLabel": "Author",
    "infoCategoryLabel": "Category",
    "infoForkedFromLabel": "Forked from",
    "infoDescriptionLabel": "Description",
    "editPatternInfo": "Edit pattern info",
    "infoVisibilityLabel": "Visibility",
    "infoPopularityLabel": "Popularity",
    "infoStarsLabel": "{{count}} stars",
    "infoForksLabel": "{{count}} forks",
    "infoForksMetaLabel": "Forks",
    "viewForks": "View forks",
    "removeStarConfirmTitle": "Remove star?",
    "removeStarConfirmMessage": "Remove your star from {{name}}?",
    "removeStarConfirmAction": "Remove star",
    "infoDescriptionSystem": "A built-in {{category}} pattern from the system library.",
    "infoDescriptionUser": "A community {{category}} pattern created by {{creator}}.",
    "infoDescriptionForked": "This version was forked from {{name}}.",
    "starsModalTitle": "Starred by ({{pattern}})",
    "forksModalTitle": "Forked by ({{pattern}})",
    "forkingPatternLabel": "forked as {{pattern}}",
    "forkedDateLabel": "Forked",
    "lastModifiedDateLabel": "Last modified",
    "noStarsYet": "No local stars yet.",
    "noForksYet": "No local forks yet.",
    "unknownForkPattern": "Unknown fork pattern",
    "youUserLabel": "You",
    "popularityBadge": "{{stars}} stars · {{forks}} forks",
    "categoryBadge": "Category: {{category}}",
    "createdByLabel": "Author:",
    "createdByBadge": "Author: {{creator}}",
    "forkedFrom": "Forked from: {{name}}",
    "originalPattern": "Original pattern",
    "categoryUncategorized": "Uncategorized",
    "categoryUnknown": "Custom",
    "visibilityPublic": "Public",
    "visibilityPrivate": "Private",
    "insertSystemPattern": "Insert System Pattern",
    "systemReadonlyHint": "System patterns are read-only. Fork to create your own editable copy.",
    "unsavedEmptyBoardNotice": "Unsaved board",
    "confirmDraftSaveTitle": "Save board",
    "confirmDraftEditTitle": "Edit board",
    "confirmDraftSaveMessage": "Save this board to your account as \"{{name}}\"?",
    "confirmDraftSaveAction": "Save board",
    "confirmDraftSaveNamePlaceholder": "Enter a board name",
    "confirmDraftSaveCategoryLabel": "Category",
    "confirmDraftSaveCategoryPlaceholder": "Select a category",
    "confirmDraftSaveDescriptionPlaceholder": "Add an optional description",
    "confirmDraftSaveTagsLabel": "Tags",
    "confirmDraftSaveTagsPlaceholder": "Add tags separated by commas",
    "confirmDraftSaveVisibilityLabel": "Visibility",
    "confirmDraftSaveCategoryHint": "Choose category and visibility, and review name/description before first save."
  },
  "diagnostics": {
    "title": "Diagnostics",
    "settings": "Settings",
    "activeRulesetLabel": "Active ruleset",
    "rulesetLabel": "Ruleset",
    "rulesetDescription": "Choose the simulation family. Conway uses B3/S23; HighLife adds B6 births (B36/S23).",
    "rulesetStandardOption": "Conway (B3/S23)",
    "rulesetHighlifeOption": "HighLife (B36/S23)",
    "rulesetCustomOption": "Custom rules",
    "rulesetCustomClassification": "Custom",
    "stabilityOverlayEnabled": "Show stability modal",
    "stabilityOverlayDescription": "When off, Continue playing suppresses the stability modal until you turn it back on.",
    "birthDeathPreviewEnabled": "Show birth/life preview",
    "birthDeathPreviewDescription": "Preview the next generation: green cells are born, and red outlines mark cells that will die.",
    "statistics": "Statistics",
    "generations": "Generations",
    "liveCells": "Live Cells",
    "liveCellsByColor": "Live Cells by Color",
    "noLiveCellsByColor": "No live cells yet.",
    "liveCellsCount": "{{count}} alive",
    "generationInteractions": "Last Generation Interactions",
    "overcrowdingDeathsByOtherColor": "Overcrowding Death Ledger by Pressure Color",
    "noOvercrowdingDeathsByOtherColor": "No cross-color overcrowding deaths in the last generation.",
    "overcrowdingDeathsCount": "{{count}} crowded out",
    "experimentalBirthsByColor": "Experimental Rule Births by Color",
    "experimentalRulesDisabled": "Enable experimental species rules to track competition births.",
    "noExperimentalBirthsByColor": "No competition-driven births in the last generation.",
    "experimentalBirthsCount": "{{count}} born",
    "tieBreakBirthsCount": "{{count}} tie-break",
    "totalBirths": "Total Births",
    "totalDeaths": "Total Deaths",
    "cellData": "Cell Data"
  },
  "about": {
    "title": "About Conway's Game of Life",
    "whatIsIt": "What is Conway's Game of Life?",
    "description1": "Conway's Game of Life is a cellular automaton devised by mathematician John Horton Conway in 1970. It's a zero-player game, meaning its evolution is determined by its initial state, requiring no further input.",
    "description2": "The game consists of a grid of cells that can be either alive or dead. Each cell interacts with its eight neighbors according to four simple rules:",
    "rule1": "Any live cell with 2-3 live neighbors survives",
    "rule2": "Any dead cell with exactly 3 live neighbors becomes alive",
    "rule3": "All other live cells die in the next generation",
    "rule4": "All other dead cells stay dead",
    "description3": "Despite these simple rules, the Game of Life can produce incredibly complex patterns and behaviors, making it a fascinating example of emergent complexity.",
    "projectTitle": "About This Project",
    "projectDescription": "This is a TypeScript implementation of Conway's Game of Life featuring:",
    "feature1": "Pure functional core logic with comprehensive unit tests",
    "feature2": "React-based interactive UI with custom Wintermuted components",
    "feature3": "Dictionary-based data structure optimized for sparse grids",
    "feature4": "Pre-loaded patterns including still lifes, oscillators, spaceships, and methuselahs",
    "feature5": "URL-based pattern sharing",
    "linksTitle": "Links",
    "viewSource": "View Source Code on GitHub",
    "wikiLink": "Conway's Game of Life on Wikipedia"
  },
  "explore": {
    "title": "Explore Patterns",
    "subtitle": "Browse common Game of Life patterns, run them instantly, fork them into your own saved set, and favorite the ones you want in your profile.",
    "searchPlaceholder": "Search patterns or categories",
    "categoryFilterLabel": "Category",
    "categoryAll": "All categories",
    "showMoreFilterOptions": "Show {{count}} more",
    "showFewerFilterOptions": "Show less",
    "noFiltersAvailable": "No filters available.",
    "sourceFilterLabel": "Source",
    "authorFilterLabel": "Author",
    "authorAll": "All authors",
    "rulesetFilterLabel": "Ruleset",
    "rulesetAll": "All rulesets",
    "tagFilterLabel": "Tags",
    "tagAll": "All tags",
    "rulesetCustom": "Custom",
    "sortLabel": "Sort patterns",
    "sortFavorites": "Top favorites",
    "sortForks": "Top forks",
    "sortName": "Name",
    "sortCategory": "Category",
    "sortUser": "User",
    "sortDirectionLabel": "Direction",
    "sortDirectionAsc": "Ascending",
    "sortDirectionDesc": "Descending",
    "groupByLabel": "Group patterns",
    "groupNone": "No grouping",
    "groupCategory": "Category",
    "groupRuleset": "Ruleset",
    "groupAuthor": "Author",
    "viewModeLabel": "View mode",
    "viewCards": "Cards",
    "viewTable": "Table",
    "allPatternsGroupLabel": "All patterns",
    "favoritesOnly": "Favorites only",
    "forkedOnly": "Forked only",
    "clearFilters": "Clear filters",
    "resultsCount": "{{count}} patterns",
    "noResultsTitle": "No patterns found",
    "noResults": "No patterns match your current filters.",
    "tablePattern": "Pattern",
    "tableSource": "Source",
    "tableCategory": "Category",
    "tableRuleset": "Ruleset",
    "tableTags": "Tags",
    "tableForks": "Forks",
    "tableStars": "Stars",
    "tableActions": "Actions",
    "categoryLabel": "Category: {{category}}",
    "popularityLabel": "Pattern popularity",
    "playPattern": "Play",
    "forkPattern": "Fork",
    "viewFavorites": "View favorites",
    "favorite": "Stars",
    "favorited": "Starred",
    "forkSuffix": "Fork"
  },
  "profile": {
    "title": "Profile",
    "subtitle": "Manage your account details and jump back into saved board states.",
    "openPlayground": "Open Playground",
    "user": {
      "name": "James Nyeholt",
      "email": "james@example.com",
      "memberSince": "April 2026",
      "timezone": "America/Los_Angeles",
      "favoritePalette": "Classic Green"
    },
    "details": {
      "memberSince": "Member since",
      "timezone": "Timezone",
      "favoritePalette": "Favorite palette",
      "boardsSaved": "Boards saved"
    },
    "savedBoards": {
      "title": "Saved Boards",
      "items": {
        "rpentomino": {
          "title": "R-Pentomino Seed",
          "updated": "Updated 2 days ago"
        },
        "gosper": {
          "title": "Gosper Gun Lab",
          "updated": "Updated 5 days ago"
        },
        "simkin": {
          "title": "Simkin Compact Stream",
          "updated": "Updated 1 week ago"
        }
      }
    },
    "recentBoards": {
      "title": "Recent Boards",
      "items": {
        "glider": {
          "title": "Glider Warmup",
          "updated": "Opened 45 minutes ago"
        },
        "blinker": {
          "title": "Blinker Timing Check",
          "updated": "Opened yesterday"
        },
        "pulsar": {
          "title": "Pulsar Sandbox",
          "updated": "Opened 3 days ago"
        }
      }
    },
    "myBoards": {
      "title": "My Gameboards",
      "empty": "No personal gameboards yet. Save a non-forked board to see it here."
    },
    "createEmptyBoard": "Create empty board",
    "emptyBoardTitle": "Empty board",
    "forkedBoards": {
      "title": "Forked Boards",
      "empty": "No forked boards yet. Fork a board in Play to see it here."
    },
    "stars": {
      "title": "Starred Boards",
      "empty": "No stars yet. Star boards in Explore to see them here."
    },
    "tableBoard": "Board",
    "tableDetails": "Origin",
    "tableUpdated": "Updated",
    "tableLiveCells": "Cells",
    "tableOriginFork": "Fork of {{name}}",
    "tableOriginMine": "Mine",
    "tableOriginLibrary": "Library",
    "forkedFrom": "Forked from {{name}}",
    "removeStar": "Remove star",
    "openBoard": "Open Board",
    "liveCells": "{{count}} live cells",
    "storageModeLabel": "Storage mode",
    "persistenceNote": "Backend persistence and account data will be wired in a future update."
  },
  "playStart": {
    "title": "Start a New Game",
    "subtitle": "Choose how you want to begin in Play.",
    "emptyTitle": "Create Empty Board",
    "emptyDescription": "Start with a blank grid and build your own pattern.",
    "emptyAction": "Start Empty",
    "randomTitle": "Random System Pattern",
    "randomDescription": "Jump in with a random built-in pattern from the system library.",
    "randomAction": "Start Random"
  },
  "settings": {
    "title": "Settings",
    "stubDescription": "Choose how temporary state is stored while backend persistence is in progress.",
    "persistenceNote": "These options are browser-only stubs until backend account settings are available.",
    "storage": {
      "title": "Browser Storage",
      "description": "Saved boards, recent boards, and profile defaults are persisted in the selected browser storage.",
      "local": "Local storage",
      "localHint": "Persists across browser restarts on this device.",
      "session": "Session storage",
      "sessionHint": "Clears when the browser tab/window session ends.",
      "clearActive": "Clear active storage data",
      "summaryTitle": "Stored Data Summary",
      "activeMode": "Active mode",
      "savedTemplates": "Saved templates",
      "savedBoards": "Saved boards",
      "recentBoards": "Recent boards",
      "profileName": "Profile name",
      "profileEmail": "Profile email",
      "savedListTitle": "Saved board titles",
      "recentListTitle": "Recent board titles",
      "emptyList": "No stored entries yet"
    },
    "messages": {
      "storageSaved": "Storage mode updated",
      "activeDataCleared": "Active storage data cleared"
    }
  },
  "signOut": {
    "title": "Sign Out",
    "stubDescription": "This is a placeholder sign-out flow.",
    "persistenceNote": "Authentication and session handling will be implemented later.",
    "returnToPlay": "Return to Play"
  },
  "messages": {
    "urlUpdated": "URL updated with current pattern",
    "urlCopied": "URL copied to clipboard",
    "urlCopyFailed": "Failed to copy URL",
    "hashCopied": "Pattern hash copied to clipboard",
    "hashCopyFailed": "Failed to copy pattern hash",
    "simulationStarted": "Simulation started",
    "simulationPaused": "Simulation paused",
    "playModeEntered": "Entered play mode",
    "editModeEntered": "Entered edit mode",
    "enterPlayModeToRun": "Enter play mode before running the simulation",
    "enterEditModeToInsertPattern": "Enter edit mode before inserting a pattern",
    "generationSpeedChanged": "Generation speed: {{level}}",
    "pauseToInsertPattern": "Pause the simulation before inserting a pattern",
    "boardUndoApplied": "Reverted the previous board change",
    "systemPatternInserted": "Inserted system pattern",
    "stampPatternSelected": "Stamp pattern selected",
    "stampPatternApplied": "Stamped pattern onto board",
    "stampRotationUpdated": "Stamp rotation: {{degrees}} deg",
    "selectStampPatternFirst": "Select a starter pattern before stamping",
    "selectionColorFilled": "Filled selected cells with {{color}}",
    "areaColorFilled": "Filled connected area with {{color}}",
    "templateSaved": "Template saved",
    "rulesetApplied": "Applied {{name}} ({{classification}})",
    "customColorUpdated": "Custom color updated: {{color}}",
    "patternForked": "Forked {{name}} to your saved patterns",
    "patternFavorited": "Added {{name}} to favorites",
    "patternUnfavorited": "Removed {{name}} from favorites",
    "stabilityDetectedTitle": "Stable Population Detected",
    "stabilityDetectedDescription": "Live cell count remained unchanged for 20 generations. Simulation paused.",
    "gameOverTitle": "Game Over",
    "gameOverDescription": "No live cells remain on the board.",
    "stabilityBannerTitle": "Stability achieved",
    "stabilityBannerDescription": "Live cell count stayed at {{liveCells}} for {{generations}} generations.",
    "returnToEditMode": "Return to edit mode",
    "continuePlaying": "Continue playing"
  },
  "patternSource": {
    "system": "System",
    "user": "User",
    "byUser": "By {{name}}"
  },
  "dialogs": {
    "confirmReset": "Confirm Reset",
    "resetMessage": "Are you sure you want to reset the game? This will clear the current state and start over.",
    "close": "Close",
    "cancel": "Cancel",
    "yesReset": "Yes, Reset"
  }
};
